/**
 * Russian counts need both the right plural form and the right case: "осталось 21 минута" but
 * "через 21 минуту". Only the singular of a feminine noun changes between the two, but getting it
 * wrong is immediately visible to a native reader, so the case is explicit rather than assumed.
 */
export type GrammaticalCase = 'nominative' | 'accusative'

interface Forms {
  /** 1, 21, 31 … */
  one: string
  /** 2–4, 22–24 … */
  few: string
  /** 0, 5–20, 25–30 … */
  many: string
  /** The accusative of `one`, when it differs. */
  oneAccusative?: string
}

const MINUTE: Forms = { one: 'минута', few: 'минуты', many: 'минут', oneAccusative: 'минуту' }
// час and день are inanimate masculine: their accusative matches the nominative.
const HOUR: Forms = { one: 'час', few: 'часа', many: 'часов' }
const DAY: Forms = { one: 'день', few: 'дня', many: 'дней' }

function plural(n: number, forms: Forms, grammaticalCase: GrammaticalCase): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 14) return forms.many

  switch (n % 10) {
    case 1:
      return grammaticalCase === 'accusative' ? (forms.oneAccusative ?? forms.one) : forms.one
    case 2:
    case 3:
    case 4:
      return forms.few
    default:
      return forms.many
  }
}

/** "2 дня 3 ч", "15 минут", "меньше минуты" — the coarsest useful precision. */
export function duration(ms: number, grammaticalCase: GrammaticalCase = 'nominative'): string {
  if (ms <= 0) return 'сейчас'

  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return 'меньше минуты'
  if (minutes < 60) return `${minutes} ${plural(minutes, MINUTE, grammaticalCase)}`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) {
    const rest = minutes % 60
    return `${hours} ${plural(hours, HOUR, grammaticalCase)}${rest ? ` ${rest} мин` : ''}`
  }

  const days = Math.floor(hours / 24)
  const restHours = hours % 24
  return `${days} ${plural(days, DAY, grammaticalCase)}${restHours ? ` ${restHours} ч` : ''}`
}

/**
 * Time until an ISO timestamp, or null when there is none — or when it has already passed, since
 * "через сейчас" is not Russian. Callers that care about "due now" ask isDue().
 */
export function until(iso: string | null, grammaticalCase: GrammaticalCase = 'nominative'): string | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return null
  return duration(ms, grammaticalCase)
}

/** True when a scheduled timestamp is already in the past — the run is due, not "in 0 minutes". */
export function isDue(iso: string | null): boolean {
  return !!iso && new Date(iso).getTime() <= Date.now()
}

/** Time since an ISO timestamp: "21 минуту назад" — always accusative. */
export function since(iso: string | null): string | null {
  if (!iso) return null
  const ms = Date.now() - new Date(iso).getTime()
  if (ms < 60_000) return 'только что'
  return `${duration(ms, 'accusative')} назад`
}

/** An interval in minutes as the panel shows it in a picker. */
export function interval(minutes: number): string {
  if (minutes < 60) return `${minutes} мин`
  const hours = minutes / 60
  return Number.isInteger(hours)
    ? `${hours} ${plural(hours, HOUR, 'nominative')}`
    : `${Math.floor(hours)} ч ${minutes % 60} мин`
}
